import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'

import { Alert, Text, ToastContext } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import UnsavedChangesModal from '@/common/components/UnsavedChangesModal/UnsavedChangesModal'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useStoreCurrencySymbol from '@/common/hooks/useStoreCurrencySymbol/useStoreCurrencySymbol'
import useUnsavedChangesGuard from '@/common/hooks/useUnsavedChangesGuard/useUnsavedChangesGuard'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import PortalsInUseList from '@/configuration/components/PortalsInUseList/PortalsInUseList'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'
import { settingsTypes } from '@/configuration/constants'

import { sectionTypes } from '../constants'
import { getConfigurationMode, getInfoWithUpdatedPreferences } from '../helpers'
import useSettingsType from '../hooks/useSettingsType/useSettingsType'
import ConfigurationRefundsInfo from './ConfigurationRefundsInfo/ConfigurationRefundsInfo'
import useRefundsConfiguration from './hooks/useRefundsConfiguration/useRefundsConfiguration'
import IssueRefundAtStatusSelector from './IssueRefundAtStatusSelector/IssueRefundAtStatusSelector'
import RefundsConfigurationSkeleton from './RefundsConfigurationSkeleton/RefundsConfigurationSkeleton'
import SortableRefundMethodsList from './SortableRefundMethodsList/SortableRefundMethodsList'
import * as constants from './constants'
import { getCustomNameLanguages, getPreferencesPayload } from './helpers'
import * as Styles from './styles'

const RefundsConfiguration = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.refundMethods,
    isUnifiedDashboard,
    setToastInfo,
  })

  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })

  const selectedStoreLanguages = info?.selectedStore?.supported_languages
  const stores = info?.stores

  const languages = useMemo(() => {
    return getCustomNameLanguages({ configurationMode, selectedStoreLanguages, stores })
  }, [configurationMode, selectedStoreLanguages, stores])

  const {
    storePreferences,
    loadingStorePreferences,
    refetchStorePreferences,
    savingConfiguration,
    hasChanges,
    revertChanges,
    issueRefundAtStatus,
    setIssueRefundAtStatus,
    availableIssueRefundStatus,
    refetchAllowedRefundMethods,
    loadingAllowedRefundMethods,
    refundMethodsSortable,
    savedRefundMethods,
    expandedRefundMethodIds,
    toggleRefundMethodExpanded,
    setRefundMethodsSortable,
    dragging,
    setDragging,
    updateRefundMethod,
    saveRefundMethods,
    errors,
    listErrors,
    entityId,
    enabledPortalsWithGlobal,
  } = useRefundsConfiguration({
    storeId: info?.selectedStore?.id,
    merchantId: info?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    configurationMode,
    languages,
  })
  const { hasPermission } = useUserPermissions()
  const canSave = hasPermission(userPermissions.editRefunds)
  const hasUnsavedChanges = canSave && (hasChanges || settingsTypeHasChanges)
  const { showUnsavedChangesModal, handleKeepEditing, handleDiscardChanges } = useUnsavedChangesGuard({
    hasUnsavedChanges,
  })
  const { storeCurrencySymbol } = useStoreCurrencySymbol()

  const preferencesAllowed = Object.keys(storePreferences || {})

  const saveSettingsAndUpdateContext = async preferencesPayload => {
    if (!isUnifiedDashboard) {
      if (settingsTypeHasChanges) {
        await onSaveSettingsType()
      }
      updateContext(info => getInfoWithUpdatedPreferences(info, preferencesPayload))
    }

    await refetchStorePreferences(entityId)
    await refetchAllowedRefundMethods(entityId)

    setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
  }

  const onSave = async () => {
    const preferencesPayload = getPreferencesPayload(issueRefundAtStatus)

    try {
      const saved = await saveRefundMethods()

      if (!saved) return

      await saveSettingsAndUpdateContext(preferencesPayload)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onSortStart = () => {
    setDragging(true)
    document.body.style.cursor = 'grabbing'
  }

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = refundMethodsSortable.findIndex(r => r.id === active.id)
    const newIndex = refundMethodsSortable.findIndex(r => r.id === over.id)

    const sortedRefundMethods = arrayMove(refundMethodsSortable, oldIndex, newIndex).map((r, index) => ({
      ...r,
      position: index,
    }))

    setRefundMethodsSortable(sortedRefundMethods)
    setDragging(false)

    document.body.style.cursor = 'inherit'
  }

  const onChangeEnabled = (refundMethod, checked) => updateRefundMethod(refundMethod.id, { is_enabled: checked })

  const handleChangeSettingsType = type => {
    setSettingsType(type)
  }

  const handleResetChanges = () => {
    revertSettingsType()
    revertChanges()
  }

  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global

  if (loadingStorePreferences || loadingAllowedRefundMethods || loadingStoreSettingsType) {
    return <RefundsConfigurationSkeleton />
  }

  return (
    <Styles.MainContainer>
      {(hasChanges || settingsTypeHasChanges) && <UnsavedChangesAlert hasPermission={canSave} />}
      {isUnifiedDashboard && (
        <Styles.PortalsInUseContainer>
          <PortalsInUseList portals={info.returnPortals} portalsInUse={enabledPortalsWithGlobal} />
        </Styles.PortalsInUseContainer>
      )}
      {!isUnifiedDashboard && info.allows_unified_dashboard && (
        <SettingsSection type={settingsType} onChange={handleChangeSettingsType} disabled={!canSave} />
      )}
      <Text type="bodyMRegular">
        {t(
          'Configura los métodos de reembolso que se mostrarán en el portal, su orden de presentación y su comportamiento'
        )}
        :
      </Text>
      {listErrors.length > 0 && <Alert type="error">{listErrors.join(' ')}</Alert>}
      <SortableRefundMethodsList
        refundMethods={refundMethodsSortable}
        savedRefundMethods={savedRefundMethods}
        expandedRefundMethodIds={expandedRefundMethodIds}
        onToggleExpanded={toggleRefundMethodExpanded}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
        dragging={dragging}
        onChangeEnabled={onChangeEnabled}
        isEditable={canSave && !isDisabledByGlobalSettings}
        isSortable={canSave}
        currency={info?.selectedStore?.currency}
        allowInstantTransactions={storePreferences?.allows_instant_transactions}
        languages={languages}
        subscriptionPlan={info?.subscription_plan}
        storeCurrencySymbol={storeCurrencySymbol}
        provider={info?.selectedStore?.provider}
        errors={errors}
        onChange={updateRefundMethod}
      />
      <ConfigurationRefundsInfo />
      {preferencesAllowed.includes(constants.preferences.issueRefundAtStatus) && (
        <>
          <Divider />
          <Text type="titleM">{t('Emisión de reembolso')}</Text>
          <Text type="bodyMRegular">{t('Configura cómo y cuándo se emitirán los reembolsos no instantáneos')}:</Text>
          <IssueRefundAtStatusSelector
            value={issueRefundAtStatus}
            onChange={setIssueRefundAtStatus}
            availableStatuses={availableIssueRefundStatus}
            disabled={isDisabledByGlobalSettings || !canSave}
          />
        </>
      )}
      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        onKeepEditing={handleKeepEditing}
        onDiscardChanges={handleDiscardChanges}
      />
      <SaveChangesButtons
        hasChanges={hasUnsavedChanges}
        onSave={onSave}
        onRevertChanges={handleResetChanges}
        loading={savingConfiguration}
        disabled={savingConfiguration}
      />
    </Styles.MainContainer>
  )
}

export default RefundsConfiguration
